import React, { useState } from 'react';
import axios from 'axios';
import './ResumeAnalyzer.css';

function ResumeAnalyzer() {
  const [resumeFile, setResumeFile] = useState(null);
  const [jobDescription, setJobDescription] = useState('');
  const [jobTitle, setJobTitle] = useState('');
  const [company, setCompany] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setResumeFile(file);
      setError(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!resumeFile) {
      setError('Please upload a resume file');
      return;
    }

    if (!jobDescription.trim()) {
      setError('Please enter a job description');
      return;
    }

    setLoading(true);
    setError(null);
    setResult(null);

    const formData = new FormData();
    formData.append('resume', resumeFile);
    formData.append('jobDescription', jobDescription);
    formData.append('jobTitle', jobTitle || 'Position');
    formData.append('company', company || 'Company');

    try {
      const response = await axios.post(
        '' + process.env.REACT_APP_API_URL + '/resume-analyzer/analyze',
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      setResult(response.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to analyze resume. Please try again.');
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  };

  const getFitColor = (percentage) => {
    if (percentage >= 80) return '#10b981'; // Green
    if (percentage >= 60) return '#f59e0b'; // Orange
    return '#ef4444'; // Red
  };

  const getFitLabel = (percentage) => {
    if (percentage >= 80) return 'Excellent Fit';
    if (percentage >= 60) return 'Good Fit';
    if (percentage >= 40) return 'Moderate Fit';
    return 'Poor Fit';
  };

  return (
    <div className="analyzer-container">
      <div className="analyzer-card">
        <h1>AI Resume Analyzer</h1>
        <p className="subtitle">Upload a resume and job description to get an AI-powered fit analysis</p>

        <form onSubmit={handleSubmit} className="analyzer-form">
          {/* Resume Upload */}
          <div className="form-group">
            <label htmlFor="resume">Resume File *</label>
            <div className="file-input-wrapper">
              <input
                type="file"
                id="resume"
                accept=".pdf,.doc,.docx"
                onChange={handleFileChange}
                disabled={loading}
              />
              {resumeFile && (
                <p className="file-name">Selected: {resumeFile.name}</p>
              )}
            </div>
          </div>

          {/* Job Title */}
          <div className="form-group">
            <label htmlFor="jobTitle">Job Title (Optional)</label>
            <input
              type="text"
              id="jobTitle"
              value={jobTitle}
              onChange={(e) => setJobTitle(e.target.value)}
              placeholder="e.g., Senior Java Developer"
              disabled={loading}
            />
          </div>

          {/* Company */}
          <div className="form-group">
            <label htmlFor="company">Company (Optional)</label>
            <input
              type="text"
              id="company"
              value={company}
              onChange={(e) => setCompany(e.target.value)}
              placeholder="e.g., Tech Corp"
              disabled={loading}
            />
          </div>

          {/* Job Description */}
          <div className="form-group">
            <label htmlFor="jobDescription">Job Description *</label>
            <textarea
              id="jobDescription"
              value={jobDescription}
              onChange={(e) => setJobDescription(e.target.value)}
              placeholder="Paste the full job description here..."
              rows="8"
              disabled={loading}
              required
            />
          </div>

          {/* Error Message */}
          {error && (
            <div className="error-message">
              {error}
            </div>
          )}

          {/* Submit Button */}
          <button
            type="submit"
            className="analyze-btn"
            disabled={loading}
          >
            {loading ? 'Analyzing...' : 'Analyze Resume'}
          </button>
        </form>

        {/* Results */}
        {result && result.success && (
          <div className="results-container">
            <h2>Analysis Results</h2>

            {/* Fit Score */}
            <div className="fit-score-card" style={{ borderColor: getFitColor(result.fitPercentage) }}>
              <div className="fit-score-circle" style={{ borderColor: getFitColor(result.fitPercentage) }}>
                <span className="fit-percentage">{result.fitPercentage.toFixed(0)}%</span>
                <span className="fit-label">{getFitLabel(result.fitPercentage)}</span>
              </div>
            </div>

            {/* Overall Assessment */}
            {result.overallAssessment && (
              <div className="result-section">
                <h3>Overall Assessment</h3>
                <p>{result.overallAssessment}</p>
              </div>
            )}

            {/* Key Strengths */}
            {result.keyStrengths && (
              <div className="result-section strengths">
                <h3>✅ Key Strengths</h3>
                <p>{result.keyStrengths}</p>
              </div>
            )}

            {/* Potential Concerns */}
            {result.potentialConcerns && (
              <div className="result-section concerns">
                <h3>⚠️ Potential Concerns</h3>
                <p>{result.potentialConcerns}</p>
              </div>
            )}

            {/* Interview Recommendations */}
            {result.interviewRecommendations && (
              <div className="result-section recommendations">
                <h3>💡 Interview Recommendations</h3>
                <p>{result.interviewRecommendations}</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default ResumeAnalyzer;